import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Download, Search, FileText, CheckCircle, XCircle, Clock, AlertTriangle, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const DOCUMENT_TYPES = ['Todos', 'DNI', 'TIP Seguridad Privada', 'Seguridad Social', 'Certificado Cuenta Bancaria', 'Otro'];

const getDocTypeBadgeColor = (type) => {
  switch (type) {
    case 'DNI': return 'bg-blue-100 text-blue-800';
    case 'TIP Seguridad Privada': return 'bg-amber-100 text-amber-800';
    case 'Seguridad Social': return 'bg-emerald-100 text-emerald-800';
    case 'Certificado Cuenta Bancaria': return 'bg-purple-100 text-purple-800';
    default: return 'bg-orange-100 text-orange-800';
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'approved': return { label: 'Aprobado', className: 'bg-green-100 text-green-700', icon: CheckCircle };
    case 'reviewed': return { label: 'Revisado', className: 'bg-blue-100 text-blue-700', icon: Clock };
    case 'rejected': return { label: 'Rechazado', className: 'bg-red-100 text-red-700', icon: XCircle };
    default: return { label: 'Pendiente', className: 'bg-orange-100 text-orange-700', icon: Clock };
  }
};

export default function CompanyDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState(['Todos', 'DNI', 'TIP Seguridad Privada', 'Seguridad Social', 'Certificado Cuenta Bancaria', 'Otro']);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('Todos');
  const [filterStatus, setFilterStatus] = useState('Todos');
  const [validationDialog, setValidationDialog] = useState(null);
  const [expiryDialog, setExpiryDialog] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    fetchDocuments();
    fetchExpiringDocs();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await API.get('/document-categories');
      if (Array.isArray(res.data)) setCategories(['Todos', ...res.data]);
    } catch {}
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await API.post(`/document-categories?name=${encodeURIComponent(newCategory.trim())}`);
      setNewCategory('');
      fetchCategories();
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await API.get('/documents');
      setDocuments(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiringDocs = async () => {
    try {
      const res = await API.get('/documents/expiring?days=30');
      setExpiringDocs(res.data);
    } catch {}
  };

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/documents/download/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const handleValidate = async (docId, status) => {
    try {
      await API.put(`/documents/${docId}/validate`, { status, notes: '' });
      setValidationDialog(null);
      fetchDocuments();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSetExpiry = async (docId) => {
    try {
      await API.put(`/documents/${docId}/expiry`, { expiry_date: expiryDate });
      setExpiryDialog(null);
      setExpiryDate('');
      fetchDocuments();
      fetchExpiringDocs();
    } catch (err) {
      console.error(err);
    }
  };

  const handleExportWorkers = async (format) => {
    try {
      const response = await API.get(`/workers/export?format=${format}`, { responseType: 'blob' });
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trabajadores.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch =
      doc.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.worker_name && doc.worker_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'Todos' || doc.document_type === filterType;
    const matchesStatus = filterStatus === 'Todos' || (doc.validation_status || 'pending') === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Documentos</h1>
          <p className="text-gray-500 mt-1 text-sm">Gestiona y valida los documentos de tus trabajadores</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExportWorkers('csv')} className="border-orange-200 hover:bg-orange-50" data-testid="export-csv-btn">
            <FileDown className="w-4 h-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExportWorkers('excel')} className="border-orange-200 hover:bg-orange-50" data-testid="export-excel-btn">
            <FileDown className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Expiring alert */}
      {expiringDocs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="expiring-docs-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 text-sm">Documentos por vencer</p>
            <p className="text-xs text-amber-700 mt-0.5">{expiringDocs.length} documento(s) vencen en los proximos 30 dias</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input placeholder="Buscar por nombre o archivo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-documents-input" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-document-type">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Todos">Todos</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="reviewed">Revisado</SelectItem>
            <SelectItem value="approved">Aprobado</SelectItem>
            <SelectItem value="rejected">Rechazado</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1.5 ml-auto items-end">
          <Input value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Nueva categoria..." className="h-9 w-40 text-sm" data-testid="new-category-input" />
          <Button size="sm" variant="outline" onClick={addCategory} disabled={!newCategory.trim()} className="h-9 border-orange-200 text-[#FF6B35] hover:bg-orange-50" data-testid="add-category-btn">+ Categoria</Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-orange-100 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-orange-100 bg-[#FFF8F4]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Trabajador</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Tipo</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Estado</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Vencimiento</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Fecha</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-400">No se encontraron documentos</TableCell>
              </TableRow>
            ) : (
              filteredDocuments.map((doc) => {
                const statusInfo = getStatusBadge(doc.validation_status);
                const isExpiring = doc.expiry_date && new Date(doc.expiry_date) <= new Date(Date.now() + 30 * 86400000);
                return (
                  <TableRow key={doc.id} className="hover:bg-orange-50/50" data-testid={`document-row-${doc.id}`}>
                    <TableCell className="font-medium text-gray-900">{doc.worker_name || '-'}</TableCell>
                    <TableCell>
                      <Badge className={getDocTypeBadgeColor(doc.document_type)}>{doc.document_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <button onClick={() => setValidationDialog(doc)} className="cursor-pointer">
                        <Badge className={`${statusInfo.className} cursor-pointer hover:opacity-80`}>{statusInfo.label}</Badge>
                      </button>
                    </TableCell>
                    <TableCell>
                      {doc.expiry_date ? (
                        <span className={`text-xs font-medium ${isExpiring ? 'text-amber-600' : 'text-gray-600'}`}>
                          {isExpiring && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                          {new Date(doc.expiry_date).toLocaleDateString('es-ES')}
                        </span>
                      ) : (
                        <button onClick={() => setExpiryDialog(doc)} className="text-xs text-orange-500 hover:text-orange-700 font-medium">
                          + Fecha
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600 text-sm">
                      {new Date(doc.created_at).toLocaleDateString('es-ES')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900 hover:bg-orange-50" onClick={() => handleDownload(doc)} data-testid={`download-document-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Validation Dialog */}
      <Dialog open={!!validationDialog} onOpenChange={() => setValidationDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Validar documento</DialogTitle>
          </DialogHeader>
          {validationDialog && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                <strong>{validationDialog.worker_name}</strong> - {validationDialog.document_type}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => handleValidate(validationDialog.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="approve-doc-btn">
                  <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                </Button>
                <Button onClick={() => handleValidate(validationDialog.id, 'rejected')} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-doc-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Rechazar
                </Button>
                <Button onClick={() => handleValidate(validationDialog.id, 'reviewed')} variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="review-doc-btn">
                  <Clock className="w-4 h-4 mr-1" /> Revisado
                </Button>
                <Button onClick={() => handleValidate(validationDialog.id, 'pending')} variant="outline" className="border-orange-200 text-orange-700 hover:bg-orange-50">
                  Pendiente
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Expiry Dialog */}
      <Dialog open={!!expiryDialog} onOpenChange={() => setExpiryDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Fecha de vencimiento</DialogTitle>
          </DialogHeader>
          {expiryDialog && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {expiryDialog.document_type} - {expiryDialog.worker_name}
              </p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Fecha de vencimiento</Label>
                <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} data-testid="expiry-date-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setExpiryDialog(null)}>Cancelar</Button>
                <Button onClick={() => handleSetExpiry(expiryDialog.id)} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white" disabled={!expiryDate}>
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
